
import { useState, useEffect } from "react";
import { Moon, Sun } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { 
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger 
} from "@/components/ui/tooltip";
import { useSidebar } from "@/components/ui/sidebar";

export function ThemeToggle() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { state } = useSidebar();

  useEffect(() => {
    // Check localStorage for theme preference with light as default
    const storedTheme = localStorage.getItem("theme");
    
    if (storedTheme === "dark") {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
    } else {
      setIsDarkMode(false);
      document.documentElement.classList.remove("dark");
    }
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (isDarkMode) {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    } else {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    }
  };

  return (
    <div className="flex items-center justify-between p-2 rounded-md hover:bg-sidebar-accent/50 transition-colors">
      {state === "expanded" && (
        <span className="text-sm text-sidebar-foreground font-medium">
          {isDarkMode ? "Dark" : "Light"}
        </span>
      )}
      <div className={cn(
        "flex items-center gap-2",
        state === "collapsed" && "mx-auto"
      )}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn(
                "flex items-center justify-center",
                isDarkMode ? "text-yellow-300" : "text-sidebar-foreground"
              )}>
                {isDarkMode ? 
                  <Moon size={16} className="drop-shadow-[0_0_4px_rgba(253,224,71,0.6)]" /> : 
                  <Sun size={16} />
                }
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" align="center" hidden={state !== "collapsed"} className="bg-popover border-border text-popover-foreground">
              {isDarkMode ? "Dark Mode" : "Light Mode"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {state === "expanded" && (
          <Switch 
            checked={isDarkMode}
            onCheckedChange={toggleDarkMode}
            aria-label="Toggle dark mode"
            className={isDarkMode ? "data-[state=checked]:bg-yellow-300/80 data-[state=checked]:text-black" : ""}
          />
        )}
      </div>
    </div>
  );
};
